/**
 * ค่าคงที่ทั้งระบบ — CLAUDE.md กำหนดว่าห้าม hardcode ซ้ำที่อื่น
 * ทุก app/worker ต้อง import จากที่นี่เท่านั้น
 */

export const SCENE_W = 1080; // native px
export const SCENE_H = 1920;
/** เส้นพื้นร่วมทุกฉาก (0.8125 × 1920) — ทำให้ตัวละครข้ามจอได้ไม่กระตุก */
export const GROUND_Y = 1560;
export const SCENES = 6; // 0 = register, 1..5 = ฐาน
export const TILE_GRID = [18, 32] as const; // ฐาน 1-3
export const TILE_W = SCENE_W / TILE_GRID[0]; // 60
export const TILE_H = SCENE_H / TILE_GRID[1]; // 60

export type SceneId = 0 | 1 | 2 | 3 | 4 | 5;
export type StyleId = 1 | 2 | 3 | 4;
export type SceneMode = "floor" | "topdown" | "platformer";

export const SCENE_IDS: readonly SceneId[] = [0, 1, 2, 3, 4, 5];

/**
 * ขนาด sprite ต่อสไตล์ (w, h ของ 1 เฟรม)
 *
 * เพิ่มเป็นสองเท่าของชุดเดิม (16×24 / 32×48 / 64×96) ตามที่เจ้าของงานสั่ง
 * บันไดความละเอียดยังคงเป็น ×2 ต่อขั้นเหมือนเดิม แค่เริ่มสูงขึ้นหนึ่งขั้น
 */
/**
 * เฟรมเป็น**จัตุรัส** ไม่ใช่ 2:3 — ท่าเตะสุดขา/ท่าตกลำตัวเอียงกว้างเกือบเท่าสูง
 * เฟรมแคบทำให้ต้องเลือกระหว่างบีบแนวนอน (ตัวลีบ) หรือย่อทั้งตัว (ตัวหด)
 * ผู้ชมเห็นตัวละครหดทุกครั้งที่กดต่อย/เตะ (D-29) · ความสูงบนจอเท่าเดิม
 * เฟรมที่กว้างขึ้นแค่ให้พื้นที่แขนขายืด
 */
export const AVATAR: Record<StyleId, readonly [number, number]> = {
  1: [48, 48],
  2: [96, 96],
  3: [192, 192],
  4: [384, 384], // anime — ขั้นบนสุดของบันไดความละเอียด (D-28)
};

/**
 * ลำดับเฟรมใน sheet — **ต้องตรงกับ POSE_NAMES ใน packages/avatar/src/charsheet.ts**
 *
 * เดิมมีแค่ idle + walk×4 · jump/fall จึงต้องยืมเฟรมยืนมาใช้ (หนี้ D6)
 * ตอนนี้มีเฟรมของตัวเองครบ รวมท่าต่อยและเตะที่เจ้าของงานขอเพิ่ม
 */
export const FRAME_ORDER = [
  "idle",
  "walk0", "walk1", "walk2", "walk3",
  "jump0", "jump1", "jump2", "jump3",
  "fall0", "fall1", "fall2", "fall3",
  "punch0", "punch1", "punch2", "punch3",
  "kick0", "kick1", "kick2", "kick3",
] as const;
export const FRAMES_PER_SHEET = FRAME_ORDER.length;

/**
 * ดัชนีเฟรมของแต่ละท่า — เอนจินใช้ตัวนี้แทนการนับเลขเอง
 * ท่าที่มีหลายเฟรมจะวนตามลำดับในอาเรย์
 */
export const FRAMES_BY_ANIM: Record<string, readonly number[]> = {
  idle: [0],
  walk: [1, 2, 3, 4],
  jump: [5, 6, 7, 8],
  fall: [9, 10, 11, 12],
  punch: [13, 14, 15, 16],
  kick: [17, 18, 19, 20],
};

/**
 * โครงฉากชุดใหม่ (D-28): ตัด GB OFFICE ออก เลื่อนฐานเดิมขึ้น เพิ่ม SAKURA VILLAGE
 * ปิดท้ายด้วยสไตล์ anime — จอ REGISTER คงเป็นฉาก 0 เหมือนเดิม
 */
export const STYLE_BY_SCENE: Record<SceneId, StyleId> = {
  0: 1,
  1: 1,
  2: 1,
  3: 2,
  4: 3,
  5: 4,
};

export const MODE_BY_SCENE: Record<SceneId, SceneMode> = {
  0: "floor",
  1: "platformer",
  2: "topdown",
  3: "platformer",
  4: "platformer",
  5: "platformer",
};

export const SCENE_NAME: Record<SceneId, string> = {
  0: "REGISTER",
  1: "SKY BRICKS",
  2: "FRUIT MAZE",
  3: "NEON BUS STOP",
  4: "SPIRIT FOREST",
  5: "SAKURA VILLAGE",
};

/** ฉาก 1-2 เป็น pixel art → image-rendering: pixelated · ฉาก 3-5 ห้ามใส่ */
export function isPixelScene(scene: SceneId): boolean {
  return scene === 1 || scene === 2;
}

/**
 * ⚠️ ความสูงตัวละครบนจอ ใช้ค่าเดียวทุกฉากไม่ได้
 * เขาวงกตฐาน 3 ทางเดินกว้าง 1 tile (60px) — ตัว 298px เข้าไม่ได้เลย
 * ดู PHYSICS.md §2
 */
export const AVATAR_H_BY_SCENE: Record<SceneId, number> = {
  0: 298,
  1: 132,
  2: 66,
  3: 298,
  4: 298,
  5: 298,
};

/** [w, h] ของ physics body — topdown เตี้ยกว่าที่ตาเห็น (แค่ช่วงเท้า) */
export const COLLIDER_BY_SCENE: Record<SceneId, readonly [number, number]> = {
  0: [100, 290],
  1: [44, 128],
  2: [40, 38],
  3: [100, 290],
  4: [100, 290],
  5: [100, 290],
};

// ---------- อัตราการส่งข้อความ ----------
export const INPUT_HZ = 30; // มือถือ -> NB
export const POS_HZ = 10; // NB -> มือถือ
export const SYNC_HZ = 15; // มือถือ -> NB ghost ตอนคาบเกี่ยว
export const STATUS_HZ = 0.2; // heartbeat 5 s

/** NB ลบผู้เล่นที่ไม่ส่ง input เกินเวลานี้ (LWT ไม่พอ เพราะ broker ไม่รู้ว่าอยู่ scene ไหน) */
export const PLAYER_TIMEOUT_MS = 2000;
/** ความกว้างโซนคาบเกี่ยว — ตัวละครกว้าง ~100px คร่อมขอบจอจริง ต้องวาดสองจอ */
export const BLEND_ZONE_PX = 170;

export const MQTT_PREFIX = "py/v1";

/** clamp ขอบโลก — ฉาก 0 ซ้ายสุด · ฉาก 5 ขวาสุด */
export const WORLD_CLAMP_LEFT = 8;
export const WORLD_CLAMP_RIGHT = SCENE_W - 8;

/** reconnect backoff (มือถือ) */
export const RECONNECT_MIN_MS = 500;
export const RECONNECT_MAX_MS = 8000;

/** idle reset — ไม่มี input นานเท่านี้ จอกลับ attract mode */
export const IDLE_RESET_MS = 90_000;

// ---------- ค่าฟิสิกส์ (native px ต่อวินาที) ----------
export const PHYS = {
  GRAVITY: 2600,
  /**
   * ความเร็วขาขึ้นตอนกระโดด — apex = JUMP_V² / (2·GRAVITY) = **272 px**
   *
   * เดิม -980 (apex 185 px) ซึ่ง **ขึ้น platform ไม่ได้เลยหลายจุด**:
   *   ฐาน 2  พื้น y=1560 → อิฐแถวล่างสุด y=1320 ห่าง 240 px
   *   ฐาน 5  กิ่งขวา y=1404 → กิ่งกลาง y=1148 ห่าง 256 px
   * ทั้งสองจุดสูงกว่า 185 px ผู้เล่นจึงตันอยู่กับพื้นทั้งฉาก
   * -1190 ให้ apex 272 px เผื่อจากช่องที่สูงสุด (256 px) อยู่ 16 px
   */
  JUMP_V: -1190,
  MAX_FALL: 1800,
  JUMP_CUT: 0.45, // ปล่อยปุ่มเร็ว → vy *= ค่านี้
  /**
   * ช่วงเวลาหลังกระโดดที่ห้ามตัดความสูง
   *
   * input มาทาง MQTT QoS 0 — ถ้าแพ็กเก็ตที่มี `j:1` หายไปแค่เฟรมเดียวหลังกระโดด
   * jump cut จะทำงานทันทีแล้วความสูงเหลือ 45% (วัดได้ 43 px จาก 185 px)
   * ซึ่งไม่พอข้ามอิฐสูง 120 px ในฐาน 2 → ตัวละครติดขวางทางออก
   * กันด้วยการไม่ตัดความสูงในช่วงต้นของการกระโดด
   */
  MIN_JUMP_MS: 130,
  COYOTE_MS: 90,
  BUFFER_MS: 120,
  RUN: 420,
  ACCEL: 3000,
  DRAG: 3600,
  AIR_DRAG: 600,
  TD_SPEED: 420, // topdown — เท่า RUN: 360 เดิมทำให้เขาวงกต "เดินทะลุยาก" (D-30)
  /** กด↓+jump แล้วปิด collision ด้านบนชั่วคราว เพื่อลงจาก one-way */
  DROP_THRU_MS: 200,
  /** walk frame ผูกกับระยะทาง ไม่ใช่เวลา — เดินเร็วขาถี่ตามธรรมชาติ */
  STRIDE_PX: 34,
} as const;

/** fixedStep 120 Hz — ตกเร็วสุด 15 px/step เทียบ tile 60 px จึงไม่ทะลุ */
export const PHYSICS_FPS = 120;

/** worldX รวม 6 ฉากเรียงกัน = 6480 px แต่แต่ละฉากมีพิกัดของตัวเอง 0..1080 */
export function worldX(scene: SceneId, x: number): number {
  return scene * SCENE_W + x;
}
